import { useState } from 'react'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import MaskedInputService from 'services/maskedInput'
import CustomerSchedule from 'components/CustomerSchedule'
import Select from 'components/Inputs/Select'
import scheduleCustomerValidator from 'validators/scheduleCustomerValidator'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { DateTime } from 'luxon'
import { Formik } from 'formik'

import {
  SubContainer,
  FormField,
  FormRow,
  Form,
  Input,
  MaskedInput,
  Title,
  InfoText,
  Button,
  WarningText,
  ErrorContainer,
} from '../styles'

/*
  Completa o agendamento criado com o serviço que o cliente escolheu no fluxo.

  O que a API devolve tem prioridade, campo a campo — o local só preenche buraco. Isso
  existe por causa do preço: é dele que sai o valor do sinal no passo de pagamento, e o
  serviço escolhido é a única fonte que temos com certeza de ter o preço que o cliente
  acabou de ver na tela.
*/
const withSelectedService = (createdSchedule, selectedSchedule) => ({
  ...createdSchedule,
  service: { ...selectedSchedule.service, ...createdSchedule.service }
})

export default function Confirmation ({
  schedule = {},
  alternativeSchedules = [],
  requiresDeposit = false,
  onConfirm: handleConfirm = () => null
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const { business, customer } = schedule
  const schedulesToCreate = alternativeSchedules.length > 0 ? alternativeSchedules : [schedule]
  const isEdit = !!schedule.id

  return (
    <SubContainer>
      <Formik
        validationSchema={scheduleCustomerValidator}
        initialValues={{
          'firstName': customer?.name.split (' ')[0] ?? '',
          'lastName': customer?.name.split (' ').slice (1).join (' ') ?? '',
          'email': customer?.email ?? '',
          'phone': customer?.phone ?? '',
          'gender': customer?.gender ? {
            label: customer?.gender, value: customer?.gender
          } : null,
          'birth': customer?.birth
            ? DateTime.fromISO (customer.birth).toFormat ('ddMMyyyy')
            : ''
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setErrorMessage (null)
          try {
            const { firstName, lastName, birth, email, phone, gender } = values
            const createdSchedules = []

            const { data: customer } = await cutileiApi.post (
              `/businesses/${business.id}/customers`, {
                name: `${firstName.trim ()} ${lastName.trim ()}`,
                birth: DateTime.fromFormat (birth, 'ddMMyyyy').toISODate (),
                email: email.trim (),
                gender: gender?.value ?? null,
                phone
              },
              { anonymous: true }
            )

            /*
              Salão que exige sinal não cria agendamento aqui: o horário é reservado e o
              agendamento nasce quando o pagamento é confirmado. O que sai daqui é o cliente
              cadastrado e o que ele pretende marcar — a reserva e a cobrança acontecem no
              passo seguinte, que é onde o meio de pagamento é escolhido.

              Editar um agendamento que já existe segue pelo caminho de sempre: não há o que
              cobrar de novo.
            */
            if (requiresDeposit && !isEdit) {
              handleConfirm ({
                customer,
                intended: schedulesToCreate.map (item => ({
                  date: item.date,
                  time: item.time,
                  serviceId: item.service.id,
                  professionalId: item.professional.id,
                  service: item.service,
                  professional: item.professional,
                  email: email.trim ()
                }))
              })

              return setSubmitting (false)
            }

            for (const schedule of schedulesToCreate) {
              const scheduleValues = {
                businessId: business.id,
                date: schedule.date,
                time: schedule.time,
                serviceId: schedule.service.id,
                professionalId: schedule.professional.id,
                scheduleId: isEdit ? schedule.id : null,
                email: email.trim ()
              }
  
              await cutileiApi.post ('/schedules/verify_availability', scheduleValues, { anonymous: true })

              if (isEdit) {
                const { data: editedSchedule } = await cutileiApi.put (
                  `/customers/${customer.id}/schedules/${schedule.id}`, scheduleValues,
                  { anonymous: true }
                )
                createdSchedules.push (withSelectedService (editedSchedule, schedule))
              } else {
                const { data: createdSchedule } = await cutileiApi.post (
                  `/customers/${customer.id}/schedules`, scheduleValues,
                  { anonymous: true }
                )
                createdSchedules.push (withSelectedService (createdSchedule, schedule))
              }
            }

            handleConfirm ({ schedules: createdSchedules })
            setSubmitting (false)
          } catch (error) {
            setSubmitting (false)
            setErrorMessage (getApiErrorMessage (error))
            logApiError (error)
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit
        }) => (
          <Form>
            <Title>Confirmação</Title>
            {schedulesToCreate.map ((schedule, index) => (
              <FormField key={index} style={{marginTop: 0, marginBottom: 5}}>
                <CustomerSchedule
                  data={schedule}
                  showCalendarScheduler={false}
                  containerStyle={{width: '100%', marginBlock: 5}}
                />
              </FormField>
            ))}
            <InfoText>Preencha suas informações abaixo</InfoText>
            <FormRow>
              <FormField>
                <Input 
                  placeholder='Nome'
                  value={values.firstName}
                  onChange={handleChange ('firstName')} 
                  onBlur={handleBlur ('firstName')}
                />
                {touched.firstName && errors.firstName && (
                  <WarningText>
                    {errors.firstName}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <Input 
                  placeholder='Sobrenome'
                  value={values.lastName}
                  onChange={handleChange ('lastName')} 
                  onBlur={handleBlur ('lastName')}
                />
                {touched.lastName && errors.lastName && (
                  <WarningText>
                    {errors.lastName}
                  </WarningText>
                )}
              </FormField>
            </FormRow>
            <FormRow>
              <FormField style={{flex: 2}}>
                <Input
                  type='email'
                  placeholder='E-mail'
                  value={values.email}
                  onChange={handleChange ('email')}
                  onBlur={handleBlur ('email')}
                />
                {touched.email && errors.email && (
                  <WarningText>
                    {errors.email}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <MaskedInput
                  name='phone'
                  placeholder='Celular'
                  options={{
                    blocks: [0, 2, 5, 4],
                    delimiters: ['(', ') ', '-'],
                    numericOnly: true
                  }}
                  value={values.phone}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('phone')}
                />
                {touched.phone && errors.phone && (
                  <WarningText>
                    {errors.phone}
                  </WarningText>
                )}
              </FormField>
            </FormRow>
            <FormRow>
              <FormField>
                <Select
                  name='gender'
                  placeholder='Gênero...'
                  value={values.gender}
                  options={[
                    {label: 'Masculino', value: 'Masculino'},
                    {label: 'Feminino', value: 'Feminino'},
                    {label: 'Não informar', value: 'Não informar'}
                  ]}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                  error={errors.gender}
                  touched={touched.gender}
                />
                {touched.gender && errors.gender && (
                  <WarningText>{errors.gender}</WarningText>
                )}
              </FormField>
              <FormField>
                <MaskedInput
                  name='birth'
                  placeholder='Nascimento'
                  options={{date: true, datePattern: ['d', 'm', 'Y']}}
                  value={values.birth}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('birth')}
                />
                {touched.birth && errors.birth && (
                  <WarningText>
                    {errors.birth}
                  </WarningText>
                )}
              </FormField>
            </FormRow>
            {errorMessage && (
              <ErrorContainer>
                <InfoText style={{color: '#FFFFFF'}}>
                  {errorMessage}
                </InfoText>
              </ErrorContainer>
            )}
            <Button onClick={handleSubmit} disabled={isSubmitting} style={{marginTop: 10, minWidth: 120}}>
              {isSubmitting ? (
                <ButtonLoading/>
              ) : (
                isEdit ? 'Confirmar alterações' : 'Agendar'
              )}
            </Button>
          </Form>
        )}
      </Formik>
    </SubContainer>
  )
}
